import { useMemo } from 'react'
import { useSearchParams } from 'react-router'
import { match } from 'ts-pattern'

import { Option } from '@blade/std-lib'

import { ExploreFeedType } from '../../model/explore-feed-type'

import type { ExploreTableFeedType } from '../../atoms/explore-table'

type ParamTab = 'trending' | 'cooking' | 'new'
const parseTableTab = (value: string): ExploreTableFeedType | undefined => {
  return match(value)
    .with('trending' satisfies ParamTab, () => ExploreFeedType.Trending)
    .with('cooking' satisfies ParamTab, () => ExploreFeedType.Cooking)
    .with('new' satisfies ParamTab, () => ExploreFeedType.New)
    .otherwise(() => undefined)
}
const tableTabToParam = (tab: ExploreTableFeedType) => {
  return match(tab)
    .returnType<ParamTab>()
    .with(ExploreFeedType.Trending, () => 'trending')
    .with(ExploreFeedType.Cooking, () => 'cooking')
    .with(ExploreFeedType.New, () => 'new')
    .exhaustive()
}

export const useExploreTableTab = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = useMemo(
    () =>
      Option.fromNullishOr(searchParams.get('table')).pipe(
        Option.flatMapNullishOr(parseTableTab),
        Option.getOrElse(() => ExploreFeedType.Trending),
      ),
    [searchParams],
  )

  const setTab = (tab: ExploreTableFeedType) => {
    setSearchParams(
      (prev) => {
        prev.set('table', tableTabToParam(tab))
        return prev
      },
      { preventScrollReset: true, replace: true },
    )
  }

  return [tab, setTab] as const
}
